import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FaStore, FaEye, FaEyeSlash, FaSync } from "react-icons/fa";
import { useAuth } from "../../hooks/useAuth";
import { useToastContext } from "../../context/ToastContext";
import styles from "./LoginPage.module.css";

function generateCaptcha() {
  return String(Math.floor(1000 + Math.random() * 9000));
}

function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoggedIn } = useAuth();
  const { addToast } = useToastContext();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [captcha, setCaptcha] = useState(generateCaptcha);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isLoggedIn) navigate("/products", { replace: true });
  }, [isLoggedIn, navigate]);

  function refreshCaptcha() {
    setCaptcha(generateCaptcha());
    setCaptchaInput("");
  }

  function handleSubmit(e) {
    e.preventDefault();
    const errs = {};

    if (!username.trim()) errs.username = "請輸入帳號";
    if (!password.trim()) errs.password = "請輸入密碼";
    if (!captchaInput.trim()) {
      errs.captcha = "請輸入驗證碼";
    } else if (captchaInput !== captcha) {
      errs.captcha = "驗證碼錯誤";
      refreshCaptcha();
    }

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    const result = login(username, password);
    if (!result.success) {
      setErrors({ form: result.error });
      refreshCaptcha();
      return;
    }

    addToast("登入成功！歡迎回來", "success");
    navigate("/products", { replace: true });
  }

  function clearError(field) {
    if (errors[field]) setErrors(e => ({ ...e, [field]: undefined }));
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <Link to="/" className={styles.logo}>
            <FaStore />
            <span>沃克貓購物網</span>
          </Link>
          <h1 className={styles.title}>會員登入</h1>
          <p className={styles.subtitle}>登入您的帳號以繼續購物</p>
        </div>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          {errors.form && (
            <div className={styles.formError}>{errors.form}</div>
          )}

          <div className={styles.field}>
            <label htmlFor="username" className={styles.label}>帳號</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={e => { setUsername(e.target.value); clearError("username"); }}
              className={`${styles.input} ${errors.username ? styles.inputError : ""}`}
              placeholder="請輸入帳號"
              autoComplete="username"
            />
            {errors.username && <p className={styles.errorMsg}>{errors.username}</p>}
          </div>

          <div className={styles.field}>
            <label htmlFor="password" className={styles.label}>密碼</label>
            <div className={styles.passwordWrapper}>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={e => { setPassword(e.target.value); clearError("password"); }}
                className={`${styles.input} ${styles.passwordInput} ${errors.password ? styles.inputError : ""}`}
                placeholder="請輸入密碼"
                autoComplete="current-password"
              />
              <button
                type="button"
                className={styles.eyeBtn}
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? "隱藏密碼" : "顯示密碼"}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.password && <p className={styles.errorMsg}>{errors.password}</p>}
          </div>

          <div className={styles.field}>
            <label htmlFor="captcha" className={styles.label}>驗證碼</label>
            <div className={styles.captchaRow}>
              <input
                id="captcha"
                type="text"
                value={captchaInput}
                onChange={e => { setCaptchaInput(e.target.value); clearError("captcha"); }}
                className={`${styles.input} ${styles.captchaInput} ${errors.captcha ? styles.inputError : ""}`}
                placeholder="請輸入右側驗證碼"
                maxLength={4}
                autoComplete="off"
              />
              <div className={styles.captchaBox} aria-label={`驗證碼：${captcha}`}>
                {captcha.split("").map((ch, i) => (
                  <span key={i} className={styles.captchaChar} style={{ transform: `rotate(${(Math.random() * 16 - 8).toFixed(1)}deg)` }}>
                    {ch}
                  </span>
                ))}
              </div>
              <button
                type="button"
                className={styles.refreshBtn}
                onClick={refreshCaptcha}
                aria-label="重新產生驗證碼"
              >
                <FaSync />
              </button>
            </div>
            {errors.captcha && <p className={styles.errorMsg}>{errors.captcha}</p>}
          </div>

          <button type="submit" className={styles.submitBtn}>
            登入
          </button>
        </form>

        <div className={styles.hint}>
          <p className={styles.hintTitle}>測試帳號</p>
          <p>帳號：<code>leon.lai</code> 密碼：<code>password123</code></p>
          <p>帳號：<code>demo.user</code> 密碼：<code>demo1234</code></p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
